import { toSvg } from 'html-to-image';

/**
 * The picture of the screen a report carries (R2). html-to-image copies every element with its computed style one
 * property at a time, the height in pixels among them, then lays the text out again in the copy; a line that wraps
 * there (a font a hair wider, a size rounded down) spills out of its frozen box over the row under it, and the
 * report shows an overlap the page never had (#9's budget line over the footnote). So the copy's boxes are let go:
 * each may grow with its text, never shrink below the height it had on screen (its height becomes its least height),
 * and a grid's rows, copied as pixels too, size to what is in them again. A box that scrolls or clips keeps its size,
 * as do pictures and fields, whose height is their own.
 */

/** Elements whose box is their own, not their text's. */
const OWN_SIZE = new Set(['img', 'svg', 'video', 'canvas', 'iframe', 'picture', 'object', 'embed', 'input', 'textarea', 'select']);

/** What the copy of one element froze, as its style says. */
export interface Frozen {
  tag: string;
  display: string;
  overflowX: string;
  overflowY: string;
  height: string;
  minHeight: string;
}

const px = (v: string): number | null => (/^-?\d+(\.\d+)?px$/.test(v.trim()) ? Number.parseFloat(v) : null);

/** How one copied box is let grow with its text: the style to set, or null to leave it as it was. */
export function letGrow(f: Frozen): Record<string, string> | null {
  const tag = f.tag.toLowerCase();
  // An inline box has no height of its own to free; one not drawn has nothing to free.
  if (OWN_SIZE.has(tag) || f.display === 'inline' || f.display === 'none' || f.display === 'contents') return null;
  if (f.overflowX !== 'visible' || f.overflowY !== 'visible') return null;
  const height = px(f.height);
  if (height === null) return null;
  const least = `${Math.max(height, px(f.minHeight) ?? 0)}px`;
  const out: Record<string, string> = { height: 'auto', 'block-size': 'auto', 'min-height': least, 'min-block-size': least };
  if (f.display === 'grid' || f.display === 'inline-grid') out['grid-template-rows'] = 'none';
  return out;
}

const XHTML = 'http://www.w3.org/1999/xhtml';
const PREFIX = 'data:image/svg+xml;charset=utf-8,';

/** The copy html-to-image made, its boxes let grow; the copy unchanged if it cannot be read. */
function unfreeze(svg: string): string {
  if (!svg.startsWith(PREFIX)) return svg;
  try {
    const doc = new DOMParser().parseFromString(decodeURIComponent(svg.slice(PREFIX.length)), 'image/svg+xml');
    if (doc.getElementsByTagName('parsererror').length) return svg;
    for (const el of Array.from(doc.getElementsByTagNameNS(XHTML, '*'))) {
      const style = (el as HTMLElement).style;
      if (!style) continue;
      const grow = letGrow({
        tag: el.localName,
        display: style.getPropertyValue('display'),
        overflowX: style.getPropertyValue('overflow-x') || 'visible',
        overflowY: style.getPropertyValue('overflow-y') || 'visible',
        height: style.getPropertyValue('height'),
        minHeight: style.getPropertyValue('min-height'),
      });
      if (grow) for (const [k, v] of Object.entries(grow)) style.setProperty(k, v);
    }
    return PREFIX + encodeURIComponent(new XMLSerializer().serializeToString(doc.documentElement));
  } catch {
    return svg;
  }
}

const image = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.decoding = 'sync';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** The window as it is now, as a JPEG data URL; null if it could not be taken in six seconds. */
export async function captureScreen(): Promise<string | null> {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
  const take = async () => {
    const svg = await toSvg(document.documentElement, {
      width,
      height,
      style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` },
      filter: (node) => !(node instanceof HTMLElement && node.dataset.feedbackUi !== undefined),
    });
    const img = await image(unfreeze(svg));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#f3f0e8';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  };
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), 6000));
  try {
    return await Promise.race([take(), timeout]);
  } catch {
    return null;
  }
}
